import * as React from 'react'
import { useState } from 'react'
import './generate-test-domain-modal.scss'
import { DomainDto } from '@halo/dtos/domain.dto'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Form, Modal, Input, Select, Skeleton } from 'antd'
import { AppService } from '@halo/services/app.service'
import { DomainService } from '@halo/services/domain.service'
import { Container } from 'typescript-ioc/es5'
import { classnames } from '@halo/_utils/classnames'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import ColorPickerPopover from '@halo/components/color-picker-popover/color-picker-popover'
import TestDomainOverlay from '@halo/components/test-domain-overlay/test-domain-overlay'
import { simpleFormErrorNotification } from '@halo/_utils/notifications'

interface IGenerateTestDomainModalProps {
    domain: DomainDto
    visible?: boolean
    loading?: boolean
    onCancel: () => void
    onOk: (domain: DomainDto) => any
}

const DEFAULT_PREFIX = 'TEST'
const DEFAULT_COLOR = '#ffbf00'

function GenerateTestDomainModal({ domain, visible, loading, onCancel, onOk }: IGenerateTestDomainModalProps) {
    const [form] = Form.useForm()
    const domainService = Container.get(DomainService)
    // Touch AppService to preserve original DI side-effect parity (no behavioral impact)
    Container.get(AppService)

    const [submitting, setSubmitting] = useState(false)
    const [prefix, setPrefix] = useState<string>(DEFAULT_PREFIX)
    const [color, setColor] = useState<string>(DEFAULT_COLOR)

    let namePlaceholder = domain?.name
    if (namePlaceholder) {
        namePlaceholder = `test.${namePlaceholder}`
    }

    const handleSubmit = async (values: any) => {
        setSubmitting(true)

        try {
            const trimmedPrefix = values.prefix?.trim() || DEFAULT_PREFIX
            const overlayColor = values.overlayColor || DEFAULT_COLOR
            const name = values.name?.trim()

            const testDto = {
                account_id: domain.account_id,
                prod_domain_id: domain.id,
                name,
                display_meta: {
                    prefix: trimmedPrefix,
                    overlay_config: {
                        color: overlayColor,
                    },
                },
            }

            const res = await domainService.create(testDto, {
                showLoadingScreen: true,
            })

            if (res.ok) {
                onOk(res.data)
                handleClose()
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }

        setSubmitting(false)
    }

    const handleOk = () => {
        form
            .validateFields()
            .then(handleSubmit)
            .catch((err) => simpleFormErrorNotification(err))
    }

    const handleClose = () => {
        form.resetFields()
        setPrefix(DEFAULT_PREFIX)
        setColor(DEFAULT_COLOR)
        onCancel()
    }

    return (
        <Modal
            className={classnames('generate-test-confirm')}
            title="Generate Test Domain"
            open={visible}
            confirmLoading={submitting}
            onOk={handleOk}
            onCancel={handleClose}
        >
            <Skeleton loading={loading} active={true} title={false}>
                <p>
                    Are you sure you want to generate a test domain for <b>{domain.display_name}</b>?
                </p>

                <Form
                    form={form}
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    className="hide-inline-errors"
                    onFinish={handleSubmit}
                >
                    <div className="generate-test-confirm">
                        <Form.Item label="Prefix">
                            <div className="generate-test-confirm-group">
                                <div className={classnames('form-layout-row')}>
                                    <Form.Item name="prefix" initialValue={DEFAULT_PREFIX} noStyle={true}>
                                        <Input.Group
                                            compact={true}
                                            size="small"
                                            className="generate-test-confirm-input"
                                        >
                                            <Select
                                                size="small"
                                                defaultValue={prefix}
                                                onSelect={(value: string) => {
                                                    setPrefix(value)
                                                    form.setFieldsValue({ prefix: value })
                                                }}
                                            >
                                                <Select.Option value="TEST">Test</Select.Option>
                                                <Select.Option value="DEV">Dev</Select.Option>
                                                <Select.Option value="QA">QA</Select.Option>
                                            </Select>
                                            <Input value={domain.display_name} disabled={true} />
                                        </Input.Group>
                                    </Form.Item>
                                    <div className={classnames('form-layout-row-sub')}>
                                        <InfoCircleOutlined />
                                        <span>
                                            Display Name will appear as ({prefix}) {domain.display_name}.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Form.Item>

                        <Form.Item
                            name="name"
                            label="Domain"
                            className="generate-test-confirm-label"
                            rules={[
                                {
                                    required: true,
                                    message: 'Domain is required.',
                                },
                            ]}
                        >
                            <Input size="small" placeholder={namePlaceholder} />
                        </Form.Item>

                        <Form.Item label="Domain Icon Color" className="generate-test-confirm-label">
                            <div className="generate-test-confirm-group">
                                <div className="color-selections">
                                    <Form.Item name="overlayColor" noStyle={true}>
                                        <ColorPickerPopover
                                            defaultColor={color}
                                            color={color}
                                            onChange={(c) => setColor(c)}
                                        />
                                    </Form.Item>
                                </div>

                                <TestDomainOverlay
                                    label={prefix}
                                    color={color}
                                    icon={domain.default_icon_url}
                                />
                            </div>
                        </Form.Item>
                    </div>
                </Form>
            </Skeleton>
        </Modal>
    )
}

export default GenerateTestDomainModal
